import { useState, useEffect } from "react";
import { settings, plan as planApi } from "@/lib/api";
import { toast } from "sonner";
import { Save, Building2, Hash, Phone, MapPin, User, HardHat, Crown, Zap, Shield, BarChart3, FileText, Users, Brain, MessageCircle } from "lucide-react";

const NICHES = [
  "Civil Construction", "Electrical", "Plumbing", "Road & Highway",
  "Building Renovation", "Water Supply", "Drainage", "Irrigation",
  "Government Furniture", "IT Infrastructure", "Other"
];

const PLAN_DISPLAY = {
  starter: { label: "Starter", color: "#888880", icon: Zap },
  professional: { label: "Professional", color: "#FF5C1A", icon: Crown },
  enterprise: { label: "Enterprise", color: "#FFB800", icon: Shield },
};

function UsageBar({ label, icon: Icon, used, limit, color = "#FF5C1A" }) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 10 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isNearLimit = !isUnlimited && limit > 0 && pct >= 80;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-widest uppercase">
          <Icon size={12} /> {label}
        </span>
        <span className={`font-mono text-xs ${isNearLimit ? 'text-[#E74C3C]' : 'text-[#F5F2E8]'}`}>
          {used}{isUnlimited ? " / Unlimited" : ` / ${limit}`}
        </span>
      </div>
      <div className="w-full h-1.5 bg-[#0A0A08] rounded-sm overflow-hidden">
        <div className="h-full transition-all rounded-sm" style={{ width: `${isUnlimited ? 100 : pct}%`, backgroundColor: isNearLimit ? '#E74C3C' : color }} />
      </div>
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState({
    company_name: "", gstin: "", whatsapp_number: "", address: "", owner_name: "", work_niche: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    Promise.all([settings.get(), planApi.get()]).then(([settingsRes, planRes]) => {
      setForm({
        company_name: settingsRes.data.company_name || "",
        gstin: settingsRes.data.gstin || "",
        whatsapp_number: settingsRes.data.whatsapp_number || "",
        address: settingsRes.data.address || "",
        owner_name: settingsRes.data.owner_name || "",
        work_niche: settingsRes.data.work_niche || "",
      });
      setPlanData(planRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settings.update(form);
      const user = JSON.parse(localStorage.getItem("contractor_os_user") || "{}");
      user.work_niche = form.work_niche;
      localStorage.setItem("contractor_os_user", JSON.stringify(user));
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-4 py-3 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444] font-body";

  if (loading) return <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div>;

  const currentPlan = planData?.plan || "starter";
  const display = PLAN_DISPLAY[currentPlan] || PLAN_DISPLAY.starter;
  const PlanIcon = display.icon;
  const limits = planData?.limits || {};
  const usage = planData?.usage || {};

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h2 className="font-heading text-3xl tracking-[2px]">SETTINGS</h2>
        <p className="text-sm text-[#888880] mt-1">Manage your company information and plan</p>
      </div>

      {/* My Plan Section */}
      <div className="bg-[#111110] border border-[#27272A] p-6 space-y-5" data-testid="my-plan-section">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl tracking-[2px] text-[#FF5C1A]">MY PLAN</h3>
          <div className="flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: display.color + '40', backgroundColor: display.color + '10' }}>
            <PlanIcon size={14} style={{ color: display.color }} />
            <span className="font-mono text-xs tracking-widest uppercase" style={{ color: display.color }} data-testid="current-plan-name">
              {display.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#0A0A08] border border-[#27272A] p-4">
            <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase mb-1">Monthly Price</div>
            <div className="font-mono text-2xl text-[#FFB800]" data-testid="plan-price">{limits.price?.toLocaleString("en-IN") || "0"}<span className="text-sm text-[#888880]">/mo</span></div>
          </div>
          <div className="bg-[#0A0A08] border border-[#27272A] p-4">
            <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase mb-1">AI Analyses</div>
            <div className="font-mono text-2xl text-[#FF5C1A]" data-testid="ai-usage">{usage.ai_analyses_this_month || 0}<span className="text-sm text-[#888880]">/{limits.ai_analyses_per_month === -1 ? "Unlimited" : limits.ai_analyses_per_month || 0}</span></div>
          </div>
        </div>

        <div className="space-y-3">
          <UsageBar label="Active Projects" icon={BarChart3} used={usage.active_projects || 0} limit={limits.active_projects || 0} />
          <UsageBar label="Workers" icon={Users} used={usage.total_workers || 0} limit={limits.workers || 0} />
          <UsageBar label="AI Analyses (this month)" icon={Brain} used={usage.ai_analyses_this_month || 0} limit={limits.ai_analyses_per_month || 0} />
          <UsageBar label="Documents" icon={FileText} used={usage.total_documents || 0} limit={limits.vault_gb === -1 ? -1 : (limits.vault_gb || 5) * 20} color="#2ECC71" />
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2 text-[#888880]">
            <div className={`w-2 h-2 rounded-full ${limits.pdf_export ? 'bg-[#2ECC71]' : 'bg-[#E74C3C]'}`} />
            <span className="font-mono tracking-widest uppercase">PDF Export</span>
          </div>
          <div className="flex items-center gap-2 text-[#888880]">
            <div className={`w-2 h-2 rounded-full ${limits.bulk_import ? 'bg-[#2ECC71]' : 'bg-[#E74C3C]'}`} />
            <span className="font-mono tracking-widest uppercase">Bulk Import</span>
          </div>
        </div>

        {currentPlan === "starter" && (
          <a
            href="https://wa.me/919876543210?text=Hi%2C%20I%20want%20to%20upgrade%20to%20Professional%20plan%20on%20Contractor%20OS"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="upgrade-plan-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors"
          >
            <MessageCircle size={14} /> Upgrade to Professional — WhatsApp Us
          </a>
        )}
        {currentPlan === "professional" && (
          <a
            href="https://wa.me/919876543210?text=Hi%2C%20I%20want%20to%20upgrade%20to%20Enterprise%20plan%20on%20Contractor%20OS"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="upgrade-plan-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#FFB800] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FF5C1A] transition-colors"
          >
            <MessageCircle size={14} /> Upgrade to Enterprise — WhatsApp Us
          </a>
        )}
      </div>

      {/* Company Details */}
      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-[#111110] border border-[#27272A] p-6 space-y-5">
          <h3 className="font-heading text-xl tracking-[2px] text-[#FF5C1A] mb-2">COMPANY DETAILS</h3>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <Building2 size={12} /> Company Name
            </label>
            <input data-testid="settings-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inputCls} placeholder="Environ Solutions Pvt Ltd" />
          </div>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <User size={12} /> Owner Name
            </label>
            <input data-testid="settings-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className={inputCls} placeholder="Adarsh Raj" />
          </div>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <HardHat size={12} /> Work Niche
            </label>
            <select data-testid="settings-work-niche" value={form.work_niche} onChange={(e) => setForm({ ...form, work_niche: e.target.value })} className={inputCls}>
              <option value="">Select your primary niche</option>
              {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <p className="text-[10px] text-[#888880] mt-1 font-mono">Tenders will be filtered by this niche by default</p>
          </div>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <Hash size={12} /> GSTIN
            </label>
            <input data-testid="settings-gstin" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className={`${inputCls} font-mono`} placeholder="22AAAAA0000A1Z5" />
          </div>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <Phone size={12} /> WhatsApp Number
            </label>
            <input data-testid="settings-whatsapp" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className={`${inputCls} font-mono`} placeholder="+91 98765 43210" />
          </div>

          <div>
            <label className="flex items-center gap-2 font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
              <MapPin size={12} /> Address
            </label>
            <textarea data-testid="settings-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${inputCls} h-24 resize-none`} placeholder="123, Industrial Area, Lucknow, UP - 226001" />
          </div>
        </div>

        <button data-testid="save-settings-btn" type="submit" disabled={saving} className="bg-[#FF5C1A] text-[#0A0A08] px-8 py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 disabled:opacity-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
          <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
        </button>
      </form>
    </div>
  );
}
